import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ApiKey,
  PaginatedResponse,
  Project,
  Translation,
  TranslationKey,
  TranslationJob,
} from '../../../helpers/types';
import { CreateApiKeyPayload } from '../../../services/apiKeysApi';
import { AddMemberPayload } from '../../../services/projectsApi';
import {
  AiTranslateKeyPayload,
  CreateKeyPayload,
  FetchKeysPayload,
  ImportKeysPayload,
  ReviewPayload,
  StartTranslateJobPayload,
  TranslationUpdatedPayload,
  UpsertTranslationPayload,
  WorkspaceState,
} from './types';

export const initialState: WorkspaceState = {
  project: null,
  loadingProject: false,
  selectedLanguage: null,
  keys: [],
  keysMeta: null,
  keysLoading: false,
  keysPage: 1,
  keysSearch: '',
  translations: {},
  translationsLoading: false,
  busyKeyId: null,
  importing: false,
  activeJob: null,
  apiKeys: [],
  apiKeysLoading: false,
  newApiKeySecret: null,
};

const noop = () => undefined;

export const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    loadWorkspaceRequest(state, _action: PayloadAction<string>) {
      state.loadingProject = true;
      state.project = null;
      state.keys = [];
      state.translations = {};
      state.keysPage = 1;
      state.keysSearch = '';
    },
    projectLoaded(state, action: PayloadAction<Project>) {
      state.project = action.payload;
      state.loadingProject = false;
      if (!state.selectedLanguage && action.payload.targetLanguages.length) {
        state.selectedLanguage = action.payload.targetLanguages[0];
      }
    },
    projectLoadFailed(state) {
      state.loadingProject = false;
    },
    projectUpdated(state, action: PayloadAction<Project>) {
      state.project = action.payload;
      if (
        state.selectedLanguage &&
        !action.payload.targetLanguages.includes(state.selectedLanguage)
      ) {
        state.selectedLanguage = action.payload.targetLanguages[0] ?? null;
      }
    },
    setSelectedLanguage(state, action: PayloadAction<string>) {
      state.selectedLanguage = action.payload;
    },

    // Keys
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchKeysRequest(state, _action: PayloadAction<FetchKeysPayload>) {
      state.keysLoading = true;
    },
    keysLoaded(
      state,
      action: PayloadAction<PaginatedResponse<TranslationKey>>,
    ) {
      state.keys = action.payload.data;
      state.keysMeta = action.payload.meta;
      state.keysPage = action.payload.meta.page;
      state.keysLoading = false;
    },
    keysFailed(state) {
      state.keysLoading = false;
    },
    setKeysSearch(state, action: PayloadAction<string>) {
      state.keysSearch = action.payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createKeyRequest(state, _action: PayloadAction<CreateKeyPayload>) {},
    keyCreated(state, action: PayloadAction<TranslationKey>) {
      state.keys.unshift(action.payload);
    },
    deleteKeyRequest(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<{ projectId: string; keyId: string }>,
    ) {},
    keyDeleted(state, action: PayloadAction<string>) {
      state.keys = state.keys.filter((k) => k.id !== action.payload);
      delete state.translations[action.payload];
    },

    // Translations
    fetchTranslationsRequest(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<{ projectId: string; language: string }>,
    ) {
      state.translationsLoading = true;
    },
    translationsLoaded(state, action: PayloadAction<Translation[]>) {
      const map: Record<string, Translation> = {};
      action.payload.forEach((t) => {
        const keyId =
          typeof t.translationKey === 'string'
            ? t.translationKey
            : t.translationKey.id;
        map[keyId] = t;
      });
      state.translations = map;
      state.translationsLoading = false;
    },
    translationsFailed(state) {
      state.translationsLoading = false;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    upsertTranslationRequest(state, action: PayloadAction<UpsertTranslationPayload>) {
      state.busyKeyId = action.payload.keyId;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    aiTranslateKeyRequest(state, action: PayloadAction<AiTranslateKeyPayload>) {
      state.busyKeyId = action.payload.keyId;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    reviewRequest(state, action: PayloadAction<ReviewPayload>) {
      state.busyKeyId = action.payload.keyId;
    },
    translationUpdated(
      state,
      action: PayloadAction<TranslationUpdatedPayload>,
    ) {
      state.translations[action.payload.keyId] = action.payload.translation;
      state.busyKeyId = null;
    },
    translationOpFailed(state) {
      state.busyKeyId = null;
    },
    // Background bulk-translation job
    startTranslateJobRequest(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<StartTranslateJobPayload>,
    ) {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchActiveJobRequest(state, _action: PayloadAction<string>) {},
    jobUpdated(state, action: PayloadAction<TranslationJob | null>) {
      state.activeJob = action.payload;
    },
    jobCleared(state) {
      state.activeJob = null;
    },

    // Key file import
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    importKeysRequest(state, _action: PayloadAction<ImportKeysPayload>) {
      state.importing = true;
    },
    importKeysDone(state) {
      state.importing = false;
    },

    // Project language / member mutations
    addLanguageRequest(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<{ projectId: string; code: string }>,
    ) {},
    removeLanguageRequest(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<{ projectId: string; code: string }>,
    ) {},
    addMemberRequest(
      state,
      _action: PayloadAction<{ projectId: string } & AddMemberPayload>,
    ) {
      noop();
    },
    removeMemberRequest(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<{ projectId: string; memberId: string }>,
    ) {},

    // API keys
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchApiKeysRequest(state, _action: PayloadAction<string>) {
      state.apiKeysLoading = true;
    },
    apiKeysLoaded(state, action: PayloadAction<ApiKey[]>) {
      state.apiKeys = action.payload;
      state.apiKeysLoading = false;
    },
    createApiKeyRequest(
      state,
      _action: PayloadAction<{ projectId: string } & CreateApiKeyPayload>,
    ) {
      noop();
    },
    apiKeyCreated(state, action: PayloadAction<ApiKey>) {
      state.newApiKeySecret = action.payload;
      state.apiKeys.unshift(action.payload);
    },
    clearNewApiKey(state) {
      state.newApiKeySecret = null;
    },
    revokeApiKeyRequest(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<{ projectId: string; apiKeyId: string }>,
    ) {},

    resetWorkspace() {
      return initialState;
    },
  },
});

export const { actions: workspaceActions } = workspaceSlice;

export const useWorkspaceSlice = () => ({ actions: workspaceSlice.actions });
